// Les surfaces neutres utilisent un token privé pour suivre le thème, avec leur couleur claire en repli.
/**
 * Composant de gestion du site vitrine (storefront)
 * 
 * Permet au marchand de :
 * - Définir son slug (sous-domaine)
 * - Personnaliser son site (logo, couleur, contacts, etc.)
 * - Voir l'URL de son site vitrine
 * 
 * Routes API utilisées:
 * - GET /api/user/storefront
 * - PUT /api/user/storefront/slug
 * - PUT /api/user/storefront/settings
 */

import React, { useState, useEffect } from 'react';
import { storefrontApi } from '../../utils/api';
import './StorefrontSettings.css';
import { getDeviseActive } from '../../utils/devise';

export default function StorefrontSettings() {
  // États pour les données
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slug, setSlug] = useState('');
  const [currentSlug, setCurrentSlug] = useState(null);
  // Numéro réellement enregistré : il protège l'activation du parcours WhatsApp des modifications non sauvegardées.
  const [savedWhatsappNumber, setSavedWhatsappNumber] = useState('');
  const [settings, setSettings] = useState({
    name: '',
    logo_url: '',
    primary_color: '#3b82f6',
    description: '',
    whatsapp: '',
    phone: '',
    email: '',
    facebook_url: '',
    instagram_url: '',
    image_ratio: '2:3',
    // Conserve le parcours historique pour les boutiques sans ces réglages.
    order_display_mode: 'page',
    show_order_email: true,
    delivery_days: [],
    order_deposit: { enabled: false, amount: 1000, header_text: '' }
  });

  // États pour les zones de livraison
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [editingZone, setEditingZone] = useState(null);
  const [isAddingZone, setIsAddingZone] = useState(false); // Contrôle l'affichage du formulaire d'ajout
  const [zoneForm, setZoneForm] = useState({ name: '', price: '', description: '', checkout_flow: 'standard' });
  const [zoneErrors, setZoneErrors] = useState({});

  // États pour la validation et les messages
  const [slugError, setSlugError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  /**
   * Charge les paramètres actuels depuis l'API au montage du composant
   */
  useEffect(() => {
    loadSettings();
  }, []);

  /**
   * Charge les paramètres depuis l'API
   */
  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await storefrontApi.getSettings();
      console.log(response);
      if (response.success && response.data) {
        setCurrentSlug(response.data.slug);
        setSlug(response.data.slug || '');

        const loadedSettings = response.data.settings || {};
        // Le numéro chargé depuis l'API est la seule source fiable pour autoriser le parcours WhatsApp.
        setSavedWhatsappNumber(String(loadedSettings.whatsapp || '').trim());
        const legacyCheckoutFlow = loadedSettings.order_deposit?.enabled === true ? 'deposit' : 'standard';
        setSettings(previousSettings => ({ ...previousSettings, ...loadedSettings }));
        // Les zones créées avant ce réglage reçoivent le comportement qui était déjà actif pour éviter un changement involontaire à la sauvegarde.
        setDeliveryZones((loadedSettings.delivery_zones || []).map(zone => ({
          ...zone,
          checkout_flow: zone.checkout_flow || legacyCheckoutFlow
        })));
      }
    } catch (error) {
      console.error('Erreur chargement paramètres:', error);
      showError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Valide le slug côté client
   */
  const validateSlug = (value) => {
    const cleanValue = value.trim().toLowerCase();

    if (!cleanValue) {
      return 'Le slug est requis';
    }

    if (cleanValue.length < 3 || cleanValue.length > 50) {
      return 'Le slug doit contenir entre 3 et 50 caractères';
    }

    if (!/^[a-z0-9-]+$/.test(cleanValue)) {
      return 'Le slug ne peut contenir que des lettres minuscules, chiffres et tirets';
    }

    const reservedSlugs = [
      'www', 'api', 'admin', 'app', 'backoffice', 'dashboard',
      'mercuri', 'shop', 'site', 'storefront', 'client', 'server',
      'pos', 'login', 'register', 'auth', 'test', 'dev', 'staging',
      'production', 'prod', 'demo'
    ];

    if (reservedSlugs.includes(cleanValue)) {
      return `Le slug "${cleanValue}" est réservé`;
    }

    return null;
  };

  /**
   * Gère le changement du slug avec validation en temps réel
   */
  const handleSlugChange = (e) => {
    const value = e.target.value.toLowerCase();
    setSlug(value);

    const error = validateSlug(value);
    setSlugError(error || '');
  };

  /**
   * Enregistre le slug
   */
  const handleSaveSlug = async () => {
    // Valider
    const error = validateSlug(slug);
    if (error) {
      setSlugError(error);
      return;
    }

    try {
      setSaving(true);
      const response = await storefrontApi.updateSlug(slug.trim().toLowerCase());

      if (response.success) {
        setCurrentSlug(response.data.slug);
        showSuccess(`Slug enregistré ! Votre site : ${response.data.url}`);
      } else {
        showError(response.error || 'Erreur lors de l\'enregistrement du slug');
      }
    } catch (error) {
      console.error('Erreur enregistrement slug:', error);
      showError(error.response?.data?.error || 'Erreur lors de l\'enregistrement du slug');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Gère le changement des paramètres
   */
  const handleSettingChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  /**
   * Ajoute ou retire un jour de livraison afin que le marchand choisisse
   * exactement les jours proposés à ses clients dans le modal storefront.
   * Une liste vide est volontairement conservée : aucun choix ne sera affiché.
   */
  const handleDeliveryDayToggle = (deliveryDay) => {
    setSettings(previousSettings => {
      const selectedDays = previousSettings.delivery_days || [];
      const deliveryDays = selectedDays.includes(deliveryDay)
        ? selectedDays.filter(day => day !== deliveryDay)
        : [...selectedDays, deliveryDay];

      return { ...previousSettings, delivery_days: deliveryDays };
    });
  };

  /** Enregistre le choix page ou modal dans le formulaire de réglages avant sauvegarde. */
  function handleOrderDisplayModeChange(event) {
    handleSettingChange('order_display_mode', event.target.value);
  }

  /** Active ou masque l'email facultatif des commandes standard et WhatsApp. */
  function handleOrderEmailChange(event) {
    handleSettingChange('show_order_email', event.target.checked);
  }

  /**
   * Enregistre les paramètres de personnalisation
   */
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await storefrontApi.updateSettings(settings);

      if (response.success) {
        // La mise à jour a réussi : le numéro saisi devient disponible pour les zones WhatsApp.
        setSavedWhatsappNumber(String(settings.whatsapp || '').trim());
        showSuccess('Paramètres enregistrés avec succès !');
      } else {
        showError(response.error || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur enregistrement paramètres:', error);
      showError(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Affiche un message de succès
   */
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setErrorMessage('');
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  /**
   * Affiche un message d'erreur
   */
  const showError = (message) => {
    setErrorMessage(message);
    setSuccessMessage('');
    setTimeout(() => setErrorMessage(''), 5000);
  };

  /**
   * Gestion des zones de livraison
   */
  const handleAddZone = () => {
    setEditingZone(null);
    setIsAddingZone(true); // Active l'affichage du formulaire d'ajout
    setZoneForm({ name: '', price: '', description: '', checkout_flow: 'standard' });
    setZoneErrors({});
  };

  const handleEditZone = (zone) => {
    setEditingZone(zone.id);
    setZoneForm({
      name: zone.name,
      price: zone.price.toString(),
      description: zone.description || '',
      checkout_flow: zone.checkout_flow || 'standard'
    });
    setZoneErrors({});
  };

  const handleDeleteZone = (zoneId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette zone ?')) {
      setDeliveryZones(prev => prev.filter(z => z.id !== zoneId));
      showSuccess('Zone supprimée');
    }
  };

  const validateZoneForm = () => {
    const errors = {};

    if (!zoneForm.name.trim()) {
      errors.name = 'Le nom est requis';
    } else if (zoneForm.name.trim().length < 2 || zoneForm.name.trim().length > 50) {
      errors.name = 'Le nom doit contenir entre 2 et 50 caractères';
    }

    const price = parseFloat(zoneForm.price);
    if (!zoneForm.price || isNaN(price) || price < 0) {
      errors.price = 'Le prix doit être un nombre >= 0';
    }

    if (!['whatsapp', 'deposit', 'standard'].includes(zoneForm.checkout_flow)) {
      errors.checkout_flow = 'Sélectionnez un parcours de commande valide';
    }
    // Une zone ne peut déclencher WhatsApp que si le numéro est déjà enregistré dans les réglages storefront.
    if (zoneForm.checkout_flow === 'whatsapp' && !savedWhatsappNumber) {
      errors.checkout_flow = 'Enregistrez d’abord un numéro WhatsApp dans les contacts de votre site vitrine';
    }

    // Vérifier les doublons (sauf si on édite la zone actuelle)
    const nameLower = zoneForm.name.trim().toLowerCase();
    const duplicate = deliveryZones.find(z =>
      z.name.toLowerCase() === nameLower && z.id !== editingZone
    );
    if (duplicate) {
      errors.name = 'Une zone avec ce nom existe déjà';
    }

    setZoneErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveZone = () => {
    if (!validateZoneForm()) return;

    const newZone = {
      id: editingZone || `zone-${Date.now()}`,
      name: zoneForm.name.trim(),
      price: parseFloat(zoneForm.price),
      description: zoneForm.description.trim() || undefined,
      checkout_flow: zoneForm.checkout_flow
    };

    if (editingZone) {
      // Modification
      setDeliveryZones(prev => prev.map(z => z.id === editingZone ? newZone : z));
      showSuccess('Zone modifiée');
    } else {
      // Ajout
      setDeliveryZones(prev => [...prev, newZone]);
      showSuccess('Zone ajoutée');
    }

    setEditingZone(null);
    setIsAddingZone(false); // Ferme le formulaire après sauvegarde
    setZoneForm({ name: '', price: '', description: '', checkout_flow: 'standard' });
  };

  const handleCancelZone = () => {
    setEditingZone(null);
    setIsAddingZone(false); // Ferme le formulaire lors de l'annulation
    setZoneForm({ name: '', price: '', description: '', checkout_flow: 'standard' });
    setZoneErrors({});
  };

  /**
   * Enregistre les zones de livraison
   */
  const handleSaveDeliveryZones = async () => {
    try {
      setSaving(true);
      const response = await storefrontApi.updateDeliveryZones(deliveryZones);

      if (response.success) {
        showSuccess('Zones de livraison enregistrées avec succès !');
      } else {
        showError(response.error || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur enregistrement zones:', error);
      showError(error.response?.data?.error || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="storefront-settings">
        <div className="loading">
          <div className="spinner"></div>
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="storefront-settings">
      <div className="settings-header">
        <h1>🏪 Site Vitrine</h1>
        <p className="subtitle">
          Personnalisez votre site vitrine et partagez-le avec vos clients
        </p>
      </div>

      {/* Messages de succès/erreur */}
      {successMessage && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          {errorMessage}
        </div>
      )}

      {/* Section 1: Adresse du site (Slug) */}
      <div className="settings-card">
        <div className="card-header">
          <h2>🔗 Adresse de votre site</h2>
          <p>Choisissez une adresse unique pour votre site vitrine</p>
        </div>

        <div className="card-body">
          <div className="form-group">
            <label htmlFor="slug">Adresse du site *</label>
            <div className="slug-input-group">
              <input
                type="text"
                id="slug"
                value={slug}
                onChange={handleSlugChange}
                placeholder="ma-boutique"
                pattern="[a-z0-9-]+"
                className={slugError ? 'error' : ''}
              />
              <span className="domain-suffix">.mercuri-shop.com</span>
            </div>
            {slugError && <p className="error-text">{slugError}</p>}
            {currentSlug && !slugError && (
              <p className="info-text">
                <i className="fas fa-link"></i>
                Votre site : <a href={`https://${currentSlug}.mercuri-shop.com`} target="_blank" rel="noopener noreferrer">
                  https://{currentSlug}.mercuri-shop.com
                </a>
              </p>
            )}
            <p className="help-text">
              • 3-50 caractères<br />
              • Lettres minuscules, chiffres et tirets uniquement<br />
              • Exemple : ma-boutique, shop-mode, boutique-2024
            </p>
          </div>

          <button
            onClick={handleSaveSlug}
            disabled={saving || !!slugError || !slug}
            className="btn btn-primary"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer l\'adresse'}
          </button>
        </div>
      </div>

      {/* Section 2: Informations de la boutique */}
      <div className="settings-card">
        <div className="card-header">
          <h2>🏷️ Informations de la boutique</h2>
          <p>Ces informations seront affichées sur votre site</p>
        </div>

        <div className="card-body">
          <div className="form-group">
            <label htmlFor="name">Nom de la boutique *</label>
            <input
              type="text"
              id="name"
              value={settings.name}
              onChange={(e) => handleSettingChange('name', e.target.value)}
              placeholder="Ma Boutique"
              maxLength={100}
            />
            <p className="help-text">2-100 caractères</p>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={settings.description}
              onChange={(e) => handleSettingChange('description', e.target.value)}
              placeholder="Découvrez nos produits de qualité..."
              rows={4}
              maxLength={500}
            />
            <p className="help-text">{settings.description.length}/500 caractères</p>
          </div>
        </div>
      </div>

      {/* Section 3: Apparence */}
      <div className="settings-card">
        <div className="card-header">
          <h2>🎨 Apparence</h2>
          <p>Personnalisez les couleurs et le logo</p>
        </div>

        <div className="card-body">
          <div className="form-group">
            <label htmlFor="logo_url">Logo (URL)</label>
            <input
              type="url"
              id="logo_url"
              value={settings.logo_url}
              onChange={(e) => handleSettingChange('logo_url', e.target.value)}
              placeholder="https://exemple.com/logo.png"
            />
            {settings.logo_url && (
              <div className="logo-preview">
                <img src={settings.logo_url} alt="Aperçu logo" onError={(e) => e.target.style.display = 'none'} />
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="primary_color">Couleur primaire</label>
            <div className="color-input-group">
              <input
                type="color"
                id="primary_color"
                value={settings.primary_color}
                onChange={(e) => handleSettingChange('primary_color', e.target.value)}
              />
              <input
                type="text"
                value={settings.primary_color}
                onChange={(e) => handleSettingChange('primary_color', e.target.value)}
                placeholder="#3b82f6"
                pattern="^#[0-9A-Fa-f]{6}$"
              />
            </div>
            <div className="color-preview" style={{ backgroundColor: settings.primary_color }}>
              Aperçu de la couleur
            </div>
          </div>
        </div>
      </div>

      {/* Section 3.5: Format des Images */}
      <div className="settings-card">
        <div className="card-header">
          <h2>🖼️ Format des Images</h2>
          <p>Choisissez le ratio d'affichage des images de produits</p>
        </div>

        <div className="card-body">
          <div className="form-group">
            <label htmlFor="image_ratio">Ratio d'affichage des images</label>
            <select
              id="image_ratio"
              value={settings.image_ratio}
              onChange={(e) => handleSettingChange('image_ratio', e.target.value)}
              className="form-select"
              style={{
                width: '100%',
                padding: '10px 14px',
                fontSize: '0.95rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                backgroundColor: 'var(--private-neutral-surface, white)',
                cursor: 'pointer'
              }}
            >
              <option value="2:3">Portrait (2:3) - Recommandé</option>
              <option value="1:1">Carré (1:1)</option>
            </select>
            <p className="help-text">
              Ce paramètre affecte l'affichage des images sur la page produits et la page détails
            </p>

            {/* Aperçu visuel */}
            <div className="ratio-preview">
              <div className="preview-item">
                <div
                  className="preview-box"
                  style={{
                    aspectRatio: '1/1',
                    backgroundColor: settings.primary_color,
                    opacity: settings.image_ratio === '1:1' ? 1 : 0.3,
                    transition: 'opacity 0.3s ease'
                  }}
                />
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>1:1 (Carré)</span>
              </div>
              <div className="preview-item">
                <div
                  className="preview-box"
                  style={{
                    aspectRatio: '2/3',
                    backgroundColor: settings.primary_color,
                    opacity: settings.image_ratio === '2:3' ? 1 : 0.3,
                    transition: 'opacity 0.3s ease'
                  }}
                />
                <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>2:3 (Portrait)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Section 4: Contacts */}
      <div className="settings-card">
        <div className="card-header">
          <h2>📞 Contacts</h2>
          <p>Informations de contact affichées dans le footer</p>
        </div>

        <div className="card-body">
          <div className="form-group">
            <label htmlFor="whatsapp">WhatsApp</label>
            <input
              type="tel"
              id="whatsapp"
              value={settings.whatsapp}
              onChange={(e) => handleSettingChange('whatsapp', e.target.value)}
              placeholder="+225 07 00 00 00 00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Téléphone</label>
            <input
              type="tel"
              id="phone"
              value={settings.phone}
              onChange={(e) => handleSettingChange('phone', e.target.value)}
              placeholder="+225 07 00 00 00 00"
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={settings.email}
              onChange={(e) => handleSettingChange('email', e.target.value)}
              placeholder="contact@boutique.com"
            />
          </div>
        </div>
      </div>

      {/* Section 5: Réseaux sociaux */}
      <div className="settings-card">
        <div className="card-header">
          <h2>🌐 Réseaux sociaux</h2>
          <p>Liens vers vos pages sur les réseaux sociaux</p>
        </div>

        <div className="card-body">
          <div className="form-group">
            <label htmlFor="facebook_url">
              <i className="fab fa-facebook"></i> Facebook
            </label>
            <input
              type="url"
              id="facebook_url"
              value={settings.facebook_url}
              onChange={(e) => handleSettingChange('facebook_url', e.target.value)}
              placeholder="https://facebook.com/maboutique"
            />
          </div>

          <div className="form-group">
            <label htmlFor="instagram_url">
              <i className="fab fa-instagram"></i> Instagram
            </label>
            <input
              type="url"
              id="instagram_url"
              value={settings.instagram_url}
              onChange={(e) => handleSettingChange('instagram_url', e.target.value)}
              placeholder="https://instagram.com/maboutique"
            />
          </div>
        </div>
      </div>

      {/* Section 6: Jours de livraison */}
      <div className="settings-card">
        <div className="card-header">
          <h2>📅 Jours de livraison</h2>
          <p>Sélectionnez les jours proposés aux clients. Laissez tout décoché pour ne rien afficher dans le modal.</p>
        </div>
        <div className="card-body">
          <fieldset className="delivery-days-fieldset">
            <legend>Jours disponibles</legend>
            <div className="delivery-days-options">
              {[
                { value: 'lundi', label: 'Lundi' },
                { value: 'mardi', label: 'Mardi' },
                { value: 'mercredi', label: 'Mercredi' },
                { value: 'jeudi', label: 'Jeudi' },
                { value: 'vendredi', label: 'Vendredi' },
                { value: 'samedi', label: 'Samedi' }
              ].map((day) => (
                <label key={day.value} className="delivery-day-option">
                  <input
                    type="checkbox"
                    checked={(settings.delivery_days || []).includes(day.value)}
                    onChange={() => handleDeliveryDayToggle(day.value)}
                  />
                  <span>{day.label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSaveSettings}
            disabled={saving}
          >
            {saving ? 'Enregistrement...' : 'Enregistrer les jours de livraison'}
          </button>
        </div>
      </div>

      {/* Réglages partagés par la fiche produit et les formulaires de commande. */}
      <div className="settings-card">
        <div className="card-header"><h2>Formulaire de commande</h2></div>
        <div className="card-body">
          <div className="form-group">
            <label htmlFor="order_display_mode">Au clic sur Commander</label>
            <select id="order_display_mode" value={settings.order_display_mode} onChange={handleOrderDisplayModeChange}>
              <option value="page">Ouvrir une page dédiée (même onglet)</option>
              <option value="modal">Ouvrir une fenêtre modale sur la fiche produit</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="show_order_email">
              <input id="show_order_email" type="checkbox" checked={settings.show_order_email !== false} onChange={handleOrderEmailChange} />
              {' '}Afficher le champ email dans le formulaire
            </label>
            <p className="help-text">L’email est facultatif pour les commandes standard et WhatsApp. Il reste affiché et obligatoire pour les commandes avec acompte, car le paiement l’exige.</p>
          </div>
          <button type="button" className="btn-primary" onClick={handleSaveSettings} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer le formulaire'}
          </button>
        </div>
      </div>

      {/* Section 7: Zones de livraison */}
      <div className="settings-card">
        <div className="card-header">
          <h2>Paiement de confirmation</h2>
          <p>Demandez un acompte avant de confirmer une commande du storefront.</p>
        </div>
        <div className="card-body">
          <div className="form-group">
            <label htmlFor="order_deposit_enabled">
              <input
                type="checkbox"
                id="order_deposit_enabled"
                checked={settings.order_deposit?.enabled === true}
                onChange={(event) => handleSettingChange('order_deposit', {
                  ...(settings.order_deposit || {}),
                  enabled: event.target.checked
                })}
              />
              {' '}Exiger un acompte pour confirmer les commandes
            </label>
          </div>
          {settings.order_deposit?.enabled && (
            <div className="form-group">
              <label htmlFor="order_deposit_amount">Montant de l’acompte</label>
              <select
                id="order_deposit_amount"
                value={settings.order_deposit?.amount || 1000}
                onChange={(event) => handleSettingChange('order_deposit', {
                  ...(settings.order_deposit || {}),
                  amount: Number(event.target.value)
                })}
              >
                <option value={1000}>1 000 F CFA</option>
                <option value={2000}>2 000 F CFA</option>
              </select>
              <p className="help-text">La commande restera en attente jusqu’au paiement Chariow.</p>

              {/* Ce champ permet au marchand de personnaliser le message affiché avant la commande. */}
              <label htmlFor="order_deposit_header_text">Texte d’en-tête</label>
              <textarea
                id="order_deposit_header_text"
                value={settings.order_deposit?.header_text || ''}
                onChange={(event) => handleSettingChange('order_deposit', {
                  ...(settings.order_deposit || {}),
                  header_text: event.target.value
                })}
                placeholder="Ex. Un acompte est requis pour confirmer votre commande."
                maxLength={220}
                rows={3}
              />
              <p className="help-text">Affiché dans le checkout, la page de commande et le modal. Laissez vide pour masquer le bandeau.</p>
            </div>
          )}
        </div>
      </div>

      {/* Section 6: Zones de livraison */}
      <div className="settings-card">
        <div className="card-header">
          <h2>🚚 Zones de livraison</h2>
          <p>Configurez vos zones de livraison et leurs tarifs</p>
        </div>

        <div className="card-body">
          {/* Liste des zones */}
          {deliveryZones.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Nom</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Prix</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Description</th>
                    <th style={{ padding: '10px', textAlign: 'left' }}>Parcours client</th>
                    <th style={{ padding: '10px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {deliveryZones.map((zone) => (
                    <tr key={zone.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '10px' }}>{zone.name}</td>
                      <td style={{ padding: '10px' }}>{zone.price.toLocaleString()} {getDeviseActive()}</td>
                      <td style={{ padding: '10px', color: '#6b7280' }}>{zone.description || '-'}</td>
                      <td style={{ padding: '10px' }}>
                        {zone.checkout_flow === 'whatsapp' ? 'WhatsApp après création' : zone.checkout_flow === 'deposit' ? 'Acompte requis' : 'Commande standard'}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right' }}>
                        <button
                          onClick={() => handleEditZone(zone)}
                          style={{
                            padding: '5px 10px',
                            marginRight: '5px',
                            backgroundColor: 'var(--private-ink, #3b82f6)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Modifier
                        </button>
                        <button
                          onClick={() => handleDeleteZone(zone.id)}
                          style={{
                            padding: '5px 10px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          Supprimer
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Formulaire d'ajout/modification */}
          {(editingZone !== null || deliveryZones.length === 0 || isAddingZone) && (
            <div style={{ padding: '15px', backgroundColor: 'var(--private-neutral-surface, #f9fafb)', borderRadius: '8px', marginBottom: '15px' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px' }}>
                {editingZone ? 'Modifier la zone' : 'Ajouter une zone'}
              </h3>

              <div className="form-group">
                <label htmlFor="zone_name">Nom de la zone *</label>
                <input
                  type="text"
                  id="zone_name"
                  value={zoneForm.name}
                  onChange={(e) => setZoneForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Ex: Cocody - Riviera"
                  className={zoneErrors.name ? 'error' : ''}
                />
                {zoneErrors.name && <p className="error-text">{zoneErrors.name}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="zone_price">Prix ({getDeviseActive()}) *</label>
                <input
                  type="number"
                  id="zone_price"
                  value={zoneForm.price}
                  onChange={(e) => setZoneForm(prev => ({ ...prev, price: e.target.value }))}
                  placeholder="1000"
                  min="0"
                  step="100"
                  className={zoneErrors.price ? 'error' : ''}
                />
                {zoneErrors.price && <p className="error-text">{zoneErrors.price}</p>}
              </div>

              <div className="form-group">
                <label htmlFor="zone_description">Description (optionnel)</label>
                <input
                  type="text"
                  id="zone_description"
                  value={zoneForm.description}
                  onChange={(e) => setZoneForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Ex: Livraison en 24h"
                  maxLength={100}
                />
              </div>

              <div className="form-group">
                <label htmlFor="zone_checkout_flow">Parcours de commande *</label>
                <select
                  id="zone_checkout_flow"
                  value={zoneForm.checkout_flow}
                  onChange={(e) => setZoneForm(prev => ({ ...prev, checkout_flow: e.target.value }))}
                  className={zoneErrors.checkout_flow ? 'error' : ''}
                >
                  <option value="standard">Commande standard — paiement à la livraison</option>
                  <option value="deposit">Acompte requis — redirection vers le paiement</option>
                  {savedWhatsappNumber && (
                    <option value="whatsapp">WhatsApp — commande créée puis contact WhatsApp</option>
                  )}
                  {/* Une zone historique conserve son choix visible, mais ne peut pas être enregistrée sans numéro configuré. */}
                  {!savedWhatsappNumber && zoneForm.checkout_flow === 'whatsapp' && (
                    <option value="whatsapp" disabled>WhatsApp — numéro à configurer</option>
                  )}
                </select>
                <p className="text-muted" style={{ marginTop: '6px', fontSize: '12px' }}>
                  Le choix appliqué ici est aussi vérifié côté serveur.
                </p>
                {!savedWhatsappNumber && (
                  <p className="text-muted" role="status" style={{ marginTop: '6px', fontSize: '12px' }}>
                    Ajoutez puis enregistrez un numéro WhatsApp dans la section Contacts pour activer cette option.
                  </p>
                )}
                {zoneErrors.checkout_flow && <p className="error-text">{zoneErrors.checkout_flow}</p>}
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleSaveZone}
                  className="btn btn-primary"
                >
                  {editingZone ? 'Modifier' : 'Ajouter'}
                </button>
                {(editingZone || isAddingZone) && (
                  <button
                    onClick={handleCancelZone}
                    className="btn btn-secondary"
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Bouton pour ajouter une nouvelle zone */}
          {editingZone === null && !isAddingZone && deliveryZones.length > 0 && (
            <button
              onClick={handleAddZone}
              className="btn btn-secondary"
            >
              <i className="fas fa-plus"></i> Ajouter une zone
            </button>
          )}

          {/* Bouton d'enregistrement des zones */}
          {deliveryZones.length > 0 && (
            <button
              onClick={handleSaveDeliveryZones}
              disabled={saving}
              className="btn btn-primary"
              style={{ marginTop: '15px' }}
            >
              {saving ? 'Enregistrement...' : 'Enregistrer les zones de livraison'}
            </button>
          )}
        </div>
      </div>

      {/* Bouton d'enregistrement global */}
      <div className="settings-actions">
        <button
          onClick={handleSaveSettings}
          disabled={saving || !settings.name}
          className="btn btn-primary btn-lg"
        >
          {saving ? 'Enregistrement en cours...' : 'Enregistrer tous les paramètres'}
        </button>

        {currentSlug && (
          <a
            href={`https://${currentSlug}.mercuri-shop.com`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-secondary btn-lg"
          >
            <i className="fas fa-external-link-alt"></i>
            Voir mon site
          </a>
        )}
      </div>
    </div>
  );
}
